import { useEffect, useRef, useState } from "react";

import { useTheme } from "@mui/material";

import { useMediaQueryBreakpoint } from "@hooks";

import { BlurredEllipse } from "./BlurredEllipse";
import { BlurredPath } from "./BlurredPath";
import { CopyBlock } from "./CopyBlock";
import { Ellipse } from "./Ellipse";
import { LinearGradient } from "./LinearGradient";
import {
  Container,
  smallDesktopScale,
  Wrapper,
  Wrapper2,
} from "./Process.styled";
import { RadialGradient } from "./RadialGradient";

interface ProcessAnimationProps {
  content: Array<{
    header: string;
    body: string;
  }>;
}

export const ProcessAnimation = ({ content }: ProcessAnimationProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollBottom, setScrollBottom] = useState(0);
  const { isDesktop, isDesktopXl, isTablet } = useMediaQueryBreakpoint();
  const {
    palette: { mode },
  } = useTheme();

  const scale = isDesktopXl ? 1 : isDesktop ? smallDesktopScale : 1;
  const scrollSpeedDivider = isDesktop ? 1 : 4;
  const baseColor = mode === "light" ? "#ffffff" : "#2b303b";
  const width = 1431;
  const height = 1944;

  const gradientStart = isDesktop
    ? Math.max(scrollTop + 100, -180)
    : isTablet
      ? Math.max(scrollTop - 400, -270)
      : Math.max(scrollTop - 200, -90);

  const gradientEnd = isDesktop
    ? scrollBottom - 200
    : isTablet
      ? gradientStart + 300
      : gradientStart + 100;

  const ellipseTrigger = 1350;

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top / scrollSpeedDivider;
      setScrollTop(-1 * top);
      setScrollBottom(-1 * top + window.innerHeight);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDesktop]);

  const isEnabled = (value: number, dontDisableAfter?: boolean) => {
    if (isDesktop || !dontDisableAfter) {
      return gradientEnd / scale > value && gradientStart / scale < value;
    } else {
      return gradientEnd / scale > value;
    }
  };

  const pathRef = useRef<SVGPathElement>(null);

  function getTransformValue(): {
    top: string;
    left: string;
    inFocus?: number;
  } {
    if (isDesktop || scrollTop <= 0) {
      return { top: "0", left: "0" };
    }

    const scaledGradientEnd = gradientEnd / scale;
    const topTransform = (x: number) => `calc(100vh / 3 - ${x}px)`;
    const leftTransform = (x: number) => `calc(100vw / 2 - ${x}px)`;

    if (scaledGradientEnd > 1950) {
      return {
        top: topTransform(1570),
        left: leftTransform(950),
        inFocus: scaledGradientEnd > 2100 ? undefined : 7,
      };
    }

    if (scaledGradientEnd > 1750) {
      return {
        top: topTransform(1550),
        left: leftTransform(400),
        inFocus: 6,
      };
    }

    if (scaledGradientEnd > 1350) {
      return {
        top: topTransform(1900),
        left: leftTransform(650),
        inFocus: 5,
      };
    }

    if (scaledGradientEnd > 755) {
      return {
        top: topTransform(900),
        left: leftTransform(400),
        inFocus: 4,
      };
    }

    if (scaledGradientEnd > 429) {
      return {
        top: topTransform(600),
        left: leftTransform(695),
        inFocus: 3,
      };
    }

    if (scaledGradientEnd > 66) {
      return { top: "0", left: leftTransform(980), inFocus: 2 };
    }

    if (scaledGradientEnd > -100) {
      return { top: "0", left: leftTransform(170), inFocus: 1 };
    }

    return { top: "0", left: leftTransform(170) };
  }

  const transformationValue = getTransformValue();

  return (
    <Wrapper ref={ref}>
      <Wrapper2>
        <Container
          top={transformationValue.top}
          left={transformationValue.left}
        >
          <CopyBlock
            index={1}
            enabled={isEnabled(0)}
            top={0}
            left={50}
            inFocus={transformationValue.inFocus}
            header={content[0].header}
            body={content[0].body}
          />

          <CopyBlock
            index={2}
            enabled={isEnabled(66)}
            top={66}
            left={900}
            inFocus={transformationValue.inFocus}
            header={content[1].header}
            body={content[1].body}
          />

          <CopyBlock
            index={3}
            enabled={isEnabled(429)}
            top={429}
            left={640}
            inFocus={transformationValue.inFocus}
            header={content[2].header}
            body={content[2].body}
          />

          <CopyBlock
            index={4}
            enabled={isEnabled(755)}
            top={755}
            left={330}
            inFocus={transformationValue.inFocus}
            header={content[3].header}
            body={content[3].body}
          />

          <CopyBlock
            index={7}
            enabled={isEnabled(ellipseTrigger, true)}
            top={1376}
            left={920}
            inFocus={transformationValue.inFocus}
            header={content[6].header}
            body={content[6].body}
          />
          <CopyBlock
            index={6}
            enabled={isEnabled(ellipseTrigger, true)}
            top={1350}
            left={320}
            inFocus={transformationValue.inFocus}
            header={content[5].header}
            body={content[5].body}
          />
          <CopyBlock
            index={5}
            enabled={isEnabled(ellipseTrigger, true)}
            top={1747}
            left={635}
            inFocus={transformationValue.inFocus}
            header={content[4].header}
            body={content[4].body}
          />

          <svg
            width={width}
            height={height}
            viewBox={`-100 -100 ${width} ${height}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <BlurredPath
              ref={pathRef}
              baseColor={baseColor}
              gradientId="url(#gradient-line)"
            />

            <g transform="translate(0 1230), scale(0.915)">
              <Ellipse variant="A" stroke={baseColor} strokeWidth="3" />
              <Ellipse variant="B" stroke={baseColor} strokeWidth="3" />
              <Ellipse variant="C" stroke={baseColor} strokeWidth="3" />

              <BlurredEllipse
                enabled={isEnabled(ellipseTrigger, true)}
                variant="A"
                gradientId="url(#gradient-eclipse-1)"
              />

              <BlurredEllipse
                enabled={isEnabled(ellipseTrigger, true)}
                variant="B"
                gradientId="url(#gradient-eclipse-2)"
              />

              <BlurredEllipse
                enabled={isEnabled(ellipseTrigger, true)}
                variant="C"
                gradientId="url(#gradient-eclipse-3)"
              />
            </g>

            <defs>
              <RadialGradient
                id="gradient-eclipse-1"
                duration={17}
                baseColor={baseColor}
              />

              <RadialGradient
                id="gradient-eclipse-2"
                duration={19}
                baseColor={baseColor}
              />

              <RadialGradient
                id="gradient-eclipse-3"
                duration={23}
                baseColor={baseColor}
              />

              <LinearGradient
                id="gradient-line"
                gradientStart={gradientStart}
                gradientEnd={gradientEnd}
                baseColor={baseColor}
              />

              <filter id="blur">
                <feGaussianBlur
                  in="SourceGraphic"
                  stdDeviation="8"
                  mode="screen"
                />
              </filter>
            </defs>
          </svg>
        </Container>
      </Wrapper2>
    </Wrapper>
  );
};
